import type { BlockSerializedChild, BlockSerializedSlotOutlet } from '#layers/block-engine/types/block';
import { deepToRaw, randomUUID } from '#layers/base/public';
import { isBlockSerializedChild, isBlockSerializedSlotOutlet } from '#layers/block-engine/utils/block/editor';

/**
 * Recursively clones a block child subtree, assigning new IDs to all cloned nodes.
 * Grid-agnostic: it knows nothing about layout positions and can be reused by any
 * layout block that needs alt-drag / copy-subtree semantics.
 *
 * @returns the new root child ID, or null if the source child was not found
 */
export function duplicateChildSubtree(
	childId: string,
	sourceChildren: Record<string, BlockSerializedChild | BlockSerializedSlotOutlet>,
	targetChildren: Record<string, BlockSerializedChild | BlockSerializedSlotOutlet>,
): string | null {
	const child = sourceChildren[childId];

	if (!child) {
		return null;
	}

	const newId = randomUUID();

	if (isBlockSerializedChild(child)) {
		const slots = Object.fromEntries(
			Object.entries(child.slots).map(([slotName, slotChildren]) => [
				slotName,
				slotChildren
					.map((slotChildId) => duplicateChildSubtree(slotChildId, sourceChildren, targetChildren))
					.filter((slotChildId): slotChildId is string => !!slotChildId),
			]),
		);

		targetChildren[newId] = {
			...structuredClone(deepToRaw(child)),
			id: newId,
			slots,
		};
	}
	else if (isBlockSerializedSlotOutlet(child)) {
		targetChildren[newId] = {
			...structuredClone(deepToRaw(child)),
			id: newId,
			fallback: child.fallback
				?.map((fallbackChildId) => duplicateChildSubtree(fallbackChildId, sourceChildren, targetChildren))
				.filter((fallbackChildId): fallbackChildId is string => !!fallbackChildId),
		};
	}
	else {
		return null;
	}

	return newId;
}
